import { ReactNode, useEffect, useRef, useState } from "react";

import { ArrowDownIcon, SpinnerIcon } from "@/assets/icons";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { useMenuPosition } from "@/hooks/ui/useMenuPosition";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import Checkbox from "./checkbox";

export interface IMultiOptionItem {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
  description?: string;
}

export const DROPDOWN_HEIGHT_ITEM = 40; // Height of each item in the dropdown

type DropdownDataItem<T extends IMultiOptionItem> = T[];

type Props<T extends IMultiOptionItem> = {
  name?: string;
  label?: string;
  labelBorder?: string;
  data: DropdownDataItem<T>;
  className?: string;
  menuListClassName?: string;
  buttonClassName?: string;
  placeholder?: string;
  defaultValue?: string[];
  isLoading?: boolean;
  disabled?: boolean;
  showLabelOnTop?: boolean;
  selectAllLabel?: string;
  showSelectAll?: boolean;
  maxSelected?: number;
  selectedCountPlaceHolder?: string;
  onSelect: (selectedOptions: string[]) => void;
  renderOption?: (option: T, checked: boolean) => JSX.Element;
};

export const DROPDOWN_ALL_VALUE = "all"; // Constant for "Select All" value

const DropdownMultiSelect = <T extends IMultiOptionItem>({
  name,
  label,
  className,
  menuListClassName,
  buttonClassName,
  data = [],
  placeholder = "Select value",
  labelBorder = placeholder,
  defaultValue = [],
  isLoading = false,
  disabled = false,
  showLabelOnTop = true,
  selectAllLabel = "Select All",
  showSelectAll = false,
  maxSelected,
  selectedCountPlaceHolder,
  renderOption,
  onSelect
}: Props<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue);

  const showMenuFromTop = useMenuPosition(
    isOpen,
    ref,
    data.length,
    DROPDOWN_HEIGHT_ITEM,
    300, // max height of the menu
    16 // padding for the menu
  );

  useOnClickOutside(ref, () => setIsOpen(false));

  // Handle toggling an individual item
  const handleToggle = (item: T) => {
    if (item.disabled) return;

    const checked = selectedValues.includes(item.value);
    let next: string[];

    if (checked) {
      next = selectedValues.filter((v) => v !== item.value);
    } else {
      // Only add if under maxSelected (or if no maxSelected limit)
      const ignoreAllOption = selectedValues.filter(
        (v) => v !== DROPDOWN_ALL_VALUE
      );
      if (maxSelected && ignoreAllOption.length >= maxSelected) return;
      next = [...ignoreAllOption, item.value];
    }

    setSelectedValues(next);
    onSelect(next);
    if (maxSelected && next.length === maxSelected) setIsOpen(false); // Close menu if maxSelected reached
  };

  // Handle Select All
  const handleSelectAll = () => {
    let next: string[];
    if (isAllSelected) next = [];
    else {
      next = [DROPDOWN_ALL_VALUE];
      setIsOpen(false); // Close menu when selecting all
    }
    setSelectedValues(next);
    onSelect(next);
  };

  const isChecked = (item: T) =>
    selectedValues.includes(item.value) || isAllSelected;

  // Disable unselected items if maxSelected reached
  const isItemDisabled = (item: T) =>
    item.disabled ||
    (!!maxSelected &&
      selectedValues.length >= maxSelected &&
      !selectedValues.includes(item.value));

  const handleShowSelectValue = () => {
    if (!selectedValues.length) return placeholder;
    if (isAllSelected) return "All";
    const checkedItems = data.filter((d) => selectedValues.includes(d.value));
    if (selectedCountPlaceHolder)
      return `${checkedItems.length} ${selectedCountPlaceHolder}`;
    return checkedItems.map((d) => d.label).join(", ");
  };

  const isAllSelected =
    Array.isArray(defaultValue) &&
    defaultValue.length === 1 &&
    defaultValue[0] === DROPDOWN_ALL_VALUE;

  // Trigger when defaultValue changes
  useEffect(() => {
    if (JSON.stringify(defaultValue) !== JSON.stringify(selectedValues))
      setSelectedValues(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return (
    <div
      className={twMerge("flex flex-col gap-2 relative", className)}
      ref={ref}
    >
      {label && <span className="text-sm font-bold">{label}</span>}
      <div className="relative inline-block text-left">
        <Button
          disabled={disabled}
          id="multi-menu-button"
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="dropdown-multiselect-menu-list"
          aria-label={
            name
              ? `Dropdown multi-select menu: ${name}`
              : "Dropdown multi-select menu"
          }
          className={twMerge(
            "flex text-left h-[40px] w-full border border-gainsboro bg-white py-2 px-3 text-txtHeading",
            "text-sm focus:outline-none overflow-hidden",
            disabled && "bg-white-smoke",
            isOpen && "border-teal-500",
            buttonClassName
          )}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span
            className={twMerge(
              "mr-auto text-xs w-full truncate",
              selectedValues.length ? "text-txtHeading" : "text-txtPlaceholder",
              disabled && "text-light-gray"
            )}
            aria-live="polite"
            aria-atomic="true"
          >
            {handleShowSelectValue()}
          </span>
          {isLoading && (
            <SpinnerIcon
              className="spin-animation"
              aria-label="Loading spinner"
            />
          )}
          <ArrowDownIcon
            className={twMerge(
              "fill-dim-gray",
              isOpen && "rotate-180",
              disabled && "fill-light-gray"
            )}
            aria-hidden="true"
            focusable="false"
          />
        </Button>
        {selectedValues.length > 0 && showLabelOnTop && (
          <label
            className={twMerge(
              "pointer-events-none absolute left-2 top-[-10px] mb-0 max-w-[90%] truncate",
              "bg-white px-2 text-[10px] text-txtBody",
              isOpen && "text-teal-500"
            )}
          >
            {labelBorder}
          </label>
        )}
        {isOpen && (
          <div
            className={twMerge(
              "z-[999] absolute w-full rounded-xl bg-white ring-1 custom-scrollbar overflow-y-auto max-h-[300px]",
              "shadow-lg ring-black/5 focus:outline-hidden p-3",
              showMenuFromTop ? "top-11" : "bottom-11",
              menuListClassName
            )}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="multi-menu-button"
          >
            <div className="flex flex-col gap-2" role="none">
              {/* Select All */}
              {showSelectAll && data.length > 0 && (
                <Checkbox
                  id="checkbox-select-all"
                  checked={isAllSelected}
                  className="text-xs text-txtHeading mb-1"
                  onChange={handleSelectAll}
                  label={selectAllLabel}
                />
              )}
              {data.length > 0 &&
                data.map((item) => (
                  <div key={item.value}>
                    {renderOption ? (
                      renderOption(item, isChecked(item))
                    ) : (
                      <Checkbox
                        id={`checkbox-${item.value}`}
                        checked={isChecked(item)}
                        className="text-xs text-txtHeading"
                        onChange={() => handleToggle(item)}
                        label={item.label}
                        disabled={isItemDisabled(item)}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DropdownMultiSelect;
